/**
 * Workflow Builder Page
 *
 * Create and edit project workflows that define how agents collaborate.
 */

import { A, useNavigate, useParams } from '@solidjs/router';
import { type Component, createResource, createSignal, For, onMount, Show } from 'solid-js';
import {
  createProjectWorkflow,
  executeWorkflow,
  fetchAgents,
  fetchProject,
  fetchProjectWorkflow,
  type ProjectWorkflowStatus,
  type ProjectWorkflowTrigger,
  type TriggerType,
  updateProjectWorkflow,
  type WorkflowExecutionResult,
} from '../services/api';

const WorkflowBuilder: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditing = () => !!params.workflowId;

  // Fetch project for context
  // Note: We wrap fetchProject to avoid SolidJS passing { value, refetching } as the retries parameter
  const [project] = createResource(() => params.id, (projectId) => fetchProject(projectId));

  // Fetch project agents for selection
  const [agentsData] = createResource(
    () => params.id,
    (projectId) => fetchAgents({ projectId, limit: 100 }),
  );

  // Fetch existing workflow if editing
  const [existingWorkflow] = createResource(
    () => params.workflowId,
    (id) => (id ? fetchProjectWorkflow(id) : null),
  );

  // Form state
  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [stepsDescription, setStepsDescription] = createSignal('');
  const [status, setStatus] = createSignal<ProjectWorkflowStatus>('draft');
  const [triggers, setTriggers] = createSignal<ProjectWorkflowTrigger[]>([]);
  const [selectedAgents, setSelectedAgents] = createSignal<string[]>([]);
  const [expectedArtifacts, setExpectedArtifacts] = createSignal('');
  const [timeoutSeconds, setTimeoutSeconds] = createSignal(3600);
  const [maxRetries, setMaxRetries] = createSignal(3);
  const [parallelExecution, setParallelExecution] = createSignal(false);
  const [category, setCategory] = createSignal('');
  const [tags, setTags] = createSignal('');

  // New trigger state
  const [newTriggerType, setNewTriggerType] = createSignal<TriggerType>('manual');
  const [newTriggerConfig, setNewTriggerConfig] = createSignal('');

  // UI state
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // Run workflow state
  const [showRunDialog, setShowRunDialog] = createSignal(false);
  const [runInput, setRunInput] = createSignal('');
  const [running, setRunning] = createSignal(false);
  const [runResult, setRunResult] = createSignal<WorkflowExecutionResult | null>(null);

  const agents = () => agentsData()?.items || [];

  // Populate form when editing
  onMount(() => {
    if (isEditing() && existingWorkflow()) {
      const wf = existingWorkflow()!;
      setName(wf.name);
      setDescription(wf.description || '');
      setStepsDescription(wf.stepsDescription || '');
      setStatus(wf.status);
      setTriggers(wf.triggers || []);
      setSelectedAgents(wf.agentsInvolved || []);
      setExpectedArtifacts(wf.expectedArtifacts?.join(', ') || '');
      setTimeoutSeconds(wf.timeoutSeconds);
      setMaxRetries(wf.maxRetries);
      setParallelExecution(wf.parallelExecution);
      setCategory(wf.category || '');
      setTags(wf.tags?.join(', ') || '');
    }
  });

  // Also watch for data changes after initial mount
  createResource(
    () => existingWorkflow(),
    (wf) => {
      if (wf && isEditing()) {
        setName(wf.name);
        setDescription(wf.description || '');
        setStepsDescription(wf.stepsDescription || '');
        setStatus(wf.status);
        setTriggers(wf.triggers || []);
        setSelectedAgents(wf.agentsInvolved || []);
        setExpectedArtifacts(wf.expectedArtifacts?.join(', ') || '');
        setTimeoutSeconds(wf.timeoutSeconds);
        setMaxRetries(wf.maxRetries);
        setParallelExecution(wf.parallelExecution);
        setCategory(wf.category || '');
        setTags(wf.tags?.join(', ') || '');
      }
    },
  );

  const addTrigger = () => {
    let config = {};
    try {
      if (newTriggerConfig().trim()) {
        config = JSON.parse(newTriggerConfig());
      }
    } catch {
      // If not valid JSON, use as simple string value
      if (newTriggerType() === 'scheduled') {
        config = { cron: newTriggerConfig() };
      } else if (newTriggerType() === 'webhook') {
        config = { path: newTriggerConfig() };
      } else if (newTriggerType() === 'event') {
        config = { pattern: newTriggerConfig() };
      }
    }

    setTriggers([...triggers(), { type: newTriggerType(), config }]);
    setNewTriggerConfig('');
  };

  const removeTrigger = (index: number) => {
    setTriggers(triggers().filter((_, i) => i !== index));
  };

  const toggleAgentSelection = (agentId: string) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId],
    );
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const data = {
        projectId: params.id,
        name: name(),
        description: description() || undefined,
        stepsDescription: stepsDescription() || undefined,
        status: status(),
        triggers: triggers(),
        agentsInvolved: selectedAgents(),
        expectedArtifacts: expectedArtifacts()
          ? expectedArtifacts()
              .split(',')
              .map((a) => a.trim())
              .filter(Boolean)
          : undefined,
        timeoutSeconds: timeoutSeconds(),
        maxRetries: maxRetries(),
        parallelExecution: parallelExecution(),
        category: category() || undefined,
        tags: tags()
          ? tags()
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
      };

      if (isEditing()) {
        await updateProjectWorkflow(params.workflowId!, data);
      } else {
        await createProjectWorkflow(data);
      }

      navigate(`/projects/${params.id}?tab=workflows`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const getTriggerTypeLabel = (type: TriggerType) => {
    const labels: Record<TriggerType, string> = {
      manual: 'Manual',
      scheduled: 'Scheduled (Cron)',
      webhook: 'Webhook',
      event: 'Event-based',
    };
    return labels[type];
  };

  const getTriggerConfigPlaceholder = (type: TriggerType) => {
    switch (type) {
      case 'scheduled':
        return '0 9 * * * (cron expression)';
      case 'webhook':
        return '/trigger/my-workflow';
      case 'event':
        return 'requirement.*.created';
      default:
        return '';
    }
  };

  const getTriggerBadgeColor = (type: TriggerType) => {
    switch (type) {
      case 'manual':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'scheduled':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'webhook':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'event':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    }
  };

  const handleRunWorkflow = async () => {
    if (!runInput().trim()) {
      setError('Please enter a prompt for the workflow');
      return;
    }

    setError(null);
    setRunning(true);
    setRunResult(null);

    try {
      const result = await executeWorkflow(params.workflowId!, {
        input: runInput(),
        enableAIPlanning: true,
        enableAgentLoop: true,
      });
      setRunResult(result);
      if (result.success) {
        // Navigate to executions or artifacts after successful run
        navigate(`/projects/${params.id}?tab=executions`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run workflow');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div class="max-w-3xl mx-auto">
      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-2">
          <A
            href={`/projects/${params.id}`}
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </A>
          <div class="flex-1">
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
              {isEditing() ? 'Edit Workflow' : 'New Workflow'}
            </h1>
            <Show when={project()}>
              <p class="text-gray-400 text-sm mt-1">Project: {project()?.name}</p>
            </Show>
          </div>
          <Show when={isEditing() && status() === 'active'}>
            <button
              type="button"
              onClick={() => setShowRunDialog(true)}
              class="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Run
            </button>
          </Show>
        </div>
      </div>

      {/* Error */}
      <Show when={error()}>
        <div class="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <p class="text-red-400">{error()}</p>
        </div>
      </Show>

      <form onSubmit={handleSubmit} class="space-y-6">
        {/* Basic Info */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
          <div class="flex items-start justify-between">
            <div>
              <h2 class="text-lg font-medium text-gray-200">Workflow Configuration</h2>
              <p class="text-gray-500 text-sm mt-1">
                Configure how your project executes when triggered.
              </p>
            </div>
            <select
              value={status()}
              onChange={(e) => setStatus(e.currentTarget.value as ProjectWorkflowStatus)}
              class="px-3 py-1.5 bg-gray-700/50 border border-gray-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">
              Name <span class="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder="e.g., Main Execution Flow"
              required
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <p class="text-xs text-gray-500 mt-1">
              A descriptive name for this workflow configuration.
            </p>
          </div>
        </div>

        {/* Workflow Steps */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h2 class="text-lg font-medium text-gray-200 mb-2">
            Workflow Steps <span class="text-red-400">*</span>
          </h2>
          <p class="text-gray-500 text-sm mb-4">
            Describe how agents collaborate in this workflow. The AI will interpret this at runtime.
          </p>

          <textarea
            value={stepsDescription()}
            onInput={(e) => setStepsDescription(e.currentTarget.value)}
            required
            placeholder={`Example:
1. When triggered, the Research Agent gathers relevant information
2. The Analysis Agent evaluates the research findings
3. The Writer Agent creates content based on the analysis
4. The Editor Agent reviews and refines the content
5. The final artifact is saved and the workflow completes`}
            rows={8}
            class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono text-sm"
          />
        </div>

        {/* Triggers */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h2 class="text-lg font-medium text-gray-200 mb-2">Triggers</h2>
          <p class="text-gray-500 text-sm mb-4">Define how this workflow is initiated.</p>

          {/* Trigger type explanation */}
          <div class="mb-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
            <div class="text-sm text-gray-300">
              {newTriggerType() === 'manual' && (
                <>
                  <strong class="text-cyan-400">Manual:</strong> Run the workflow on-demand by clicking the "Run Project" button or via API. Best for ad-hoc tasks and testing.
                </>
              )}
              {newTriggerType() === 'scheduled' && (
                <>
                  <strong class="text-blue-400">Scheduled:</strong> Automatically runs at specified times using cron expressions. Example: <code class="text-gray-400">0 9 * * *</code> runs daily at 9 AM. Best for recurring reports or data processing.
                </>
              )}
              {newTriggerType() === 'webhook' && (
                <>
                  <strong class="text-purple-400">Webhook:</strong> Triggered by external systems via HTTP POST to a unique URL. Best for integrations with third-party services (Slack, GitHub, etc).
                </>
              )}
              {newTriggerType() === 'event' && (
                <>
                  <strong class="text-cyan-400">Event-based:</strong> Triggered by internal events like new requirements or completed tasks. Pattern format: <code class="text-gray-400">resource.action</code> (e.g., <code class="text-gray-400">requirement.created</code>).
                </>
              )}
            </div>
          </div>

          {/* Add trigger */}
          <div class="flex gap-2 mb-4">
            <select
              value={newTriggerType()}
              onChange={(e) => setNewTriggerType(e.currentTarget.value as TriggerType)}
              class="px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="manual">Manual</option>
              <option value="scheduled">Scheduled</option>
              <option value="webhook">Webhook</option>
              <option value="event">Event</option>
            </select>
            <Show when={newTriggerType() !== 'manual'}>
              <input
                type="text"
                value={newTriggerConfig()}
                onInput={(e) => setNewTriggerConfig(e.currentTarget.value)}
                placeholder={getTriggerConfigPlaceholder(newTriggerType())}
                class="flex-1 px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </Show>
            <button
              type="button"
              onClick={addTrigger}
              class="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              Add
            </button>
          </div>

          {/* Triggers list */}
          <Show when={triggers().length > 0}>
            <div class="space-y-2">
              <For each={triggers()}>
                {(trigger, index) => (
                  <div class="flex items-center gap-3 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${getTriggerBadgeColor(
                        trigger.type,
                      )}`}
                    >
                      {getTriggerTypeLabel(trigger.type)}
                    </span>
                    <Show when={Object.keys(trigger.config).length > 0}>
                      <code class="text-sm text-gray-400 font-mono">
                        {JSON.stringify(trigger.config)}
                      </code>
                    </Show>
                    <div class="flex-1" />
                    <button
                      type="button"
                      onClick={() => removeTrigger(index())}
                      class="text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>

          <Show when={triggers().length === 0}>
            <div class="text-center py-4 text-gray-500">
              <p>No triggers defined. Add at least one trigger.</p>
            </div>
          </Show>
        </div>

        {/* Agents Involved */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <h2 class="text-lg font-medium text-gray-200 mb-2">Agents Involved</h2>
          <p class="text-gray-500 text-sm mb-4">
            Select which agents participate in this workflow.
          </p>

          <Show when={agents().length > 0}>
            <div class="grid grid-cols-2 gap-2">
              <For each={agents()}>
                {(agent) => (
                  <button
                    type="button"
                    onClick={() => toggleAgentSelection(agent.id)}
                    class={`p-3 rounded-lg border text-left transition-colors ${
                      selectedAgents().includes(agent.id)
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                        : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    <div class="font-medium">{agent.name}</div>
                    <div class="text-xs text-gray-500 truncate">
                      {agent.purpose || 'No purpose defined'}
                    </div>
                  </button>
                )}
              </For>
            </div>
          </Show>

          <Show when={agents().length === 0}>
            <div class="text-center py-4 text-gray-500">
              <p>No agents in this project yet.</p>
              <A
                href={`/agents/new?projectId=${params.id}`}
                class="text-cyan-400 hover:text-cyan-300"
              >
                Create an agent
              </A>
            </div>
          </Show>
        </div>

        {/* Execution Settings */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
          <h2 class="text-lg font-medium text-gray-200 mb-4">Execution Settings</h2>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1.5">
                Timeout (seconds)
              </label>
              <input
                type="number"
                value={timeoutSeconds()}
                onInput={(e) => setTimeoutSeconds(parseInt(e.currentTarget.value, 10) || 3600)}
                min={60}
                max={86400}
                class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <p class="text-xs text-gray-500 mt-1">
                {Math.floor(timeoutSeconds() / 3600)}h {Math.floor((timeoutSeconds() % 3600) / 60)}m
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1.5">Max Retries</label>
              <input
                type="number"
                value={maxRetries()}
                onInput={(e) => setMaxRetries(parseInt(e.currentTarget.value, 10) || 3)}
                min={0}
                max={10}
                class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>

          <div>
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={parallelExecution()}
                onChange={(e) => setParallelExecution(e.currentTarget.checked)}
                class="w-5 h-5 rounded border-gray-600 bg-gray-700 text-cyan-500 focus:ring-cyan-500"
              />
              <div>
                <span class="text-gray-200">Allow Parallel Execution</span>
                <p class="text-xs text-gray-500">Agents can run concurrently when possible</p>
              </div>
            </label>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-1.5">
              Expected Artifacts (comma-separated)
            </label>
            <input
              type="text"
              value={expectedArtifacts()}
              onInput={(e) => setExpectedArtifacts(e.currentTarget.value)}
              placeholder="report, analysis, summary"
              class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>

        {/* Actions */}
        <div class="flex gap-4">
          <A
            href={`/projects/${params.id}`}
            class="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors text-center"
          >
            Cancel
          </A>
          <button
            type="submit"
            disabled={saving() || !name().trim() || !stepsDescription().trim()}
            class="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium rounded-xl transition-all disabled:opacity-50"
          >
            {saving() ? 'Saving...' : isEditing() ? 'Update Workflow' : 'Create Workflow'}
          </button>
        </div>
      </form>

      {/* Run Workflow Dialog */}
      <Show when={showRunDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-lg mx-4">
            <h3 class="text-xl font-semibold text-white mb-4">Run Workflow</h3>
            <p class="text-gray-400 text-sm mb-4">
              Enter a prompt to start the workflow. The agents will collaborate to complete the task.
            </p>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-300 mb-1.5">Prompt</label>
              <textarea
                value={runInput()}
                onInput={(e) => setRunInput(e.currentTarget.value)}
                placeholder="e.g., Create a dashboard showing sales metrics for Q4 2025"
                rows={4}
                class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={running()}
              />
            </div>

            <Show when={runResult()}>
              <div
                class={`mb-4 p-4 rounded-lg ${
                  runResult()?.success
                    ? 'bg-green-500/20 border border-green-500/50'
                    : 'bg-red-500/20 border border-red-500/50'
                }`}
              >
                <p class={runResult()?.success ? 'text-green-400' : 'text-red-400'}>
                  {runResult()?.success
                    ? 'Workflow executed successfully!'
                    : runResult()?.error || 'Workflow execution failed'}
                </p>
              </div>
            </Show>

            <div class="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowRunDialog(false);
                  setRunInput('');
                  setRunResult(null);
                }}
                disabled={running()}
                class="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRunWorkflow}
                disabled={running() || !runInput().trim()}
                class="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Show when={running()}>
                  <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </Show>
                {running() ? 'Running...' : 'Run Workflow'}
              </button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default WorkflowBuilder;
